import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Cloud, RotateCw, Settings } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { buttonVariants } from "@/components/ui/button";
import { Table, TableWrapper } from "@/components/ui/table";
import { StateMachine } from "@/components/data-display/state-machine";
import { Field, Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { DEP_ENTRIES, DEP_TABLE } from "@/data/mock";
import type { DepEntry } from "@/types";

const ENTRY_ICONS: Record<DepEntry["type"], React.ReactNode> = {
  sandbox: <Cloud aria-hidden className="size-7 text-ink" />,
  dedicated: <Settings aria-hidden className="size-7 text-ink" />,
  existing: <RotateCw aria-hidden className="size-7 text-ink" />,
};

const TAG_VARIANT: Record<string, "neutral" | "info" | "success" | "warning"> = {
  neutral: "neutral",
  info: "info",
  success: "success",
  warning: "warning",
};

function EntryCard({
  entry,
  selected,
  onSelect,
  actions,
}: {
  entry: DepEntry;
  selected: boolean;
  onSelect: () => void;
  actions: React.ReactNode;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        "flex cursor-pointer flex-col gap-3 rounded-[10px] border bg-surface p-[22px] text-left transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary-soft",
        selected ? "border-primary bg-selected-soft" : "border-line hover:border-primary"
      )}
    >
      {ENTRY_ICONS[entry.type]}
      <div className="text-[15px] font-extrabold text-ink">{entry.title}</div>
      <p className="m-0 text-[13px] leading-relaxed text-muted">{entry.description}</p>
      <div className="flex flex-wrap gap-1.5">
        {entry.tags.map((t) => (
          <Badge key={t.label} variant={TAG_VARIANT[t.variant] ?? "neutral"} size="sm">
            {t.label}
          </Badge>
        ))}
      </div>
      <div className="mt-auto flex flex-col gap-2.5">{actions}</div>
    </div>
  );
}

export default function DeploymentEntryPage() {
  const [selectedId, setSelectedId] = useState("dedicated");
  const navigate = useNavigate();

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-2xl font-bold leading-[1.2] text-ink">Deployment Entry Selector</h1>
          <p className="text-xs text-muted">DEP-00 · DEP-001 · Harbor Trust Bank, N.A.</p>
          <StateMachine current="preflight-running" />
        </div>
      </div>

      <div className="mb-7 grid grid-cols-1 gap-[18px] lg:grid-cols-3">
        {DEP_ENTRIES.map((entry) => {
          let actions: React.ReactNode;
          if (entry.type === "sandbox") {
            actions = (
              <Link
                to="/deployment"
                className={cn(
                  buttonVariants({ variant: "secondary" }),
                  "w-full"
                )}
                onClick={(e) => e.stopPropagation()}
              >
                Launch Sandbox
              </Link>
            );
          } else if (entry.type === "dedicated") {
            actions = (
              <Link
                to="/deployment"
                className={cn(buttonVariants({ variant: "primary" }), "w-full")}
                onClick={(e) => e.stopPropagation()}
              >
                Begin Deployment
              </Link>
            );
          } else {
            actions = (
              <div className="flex flex-col gap-2.5" onClick={(e) => e.stopPropagation()}>
                <Field label="Deployment endpoint URL" htmlFor={`endpoint-${entry.id}`}>
                  <Input
                    id={`endpoint-${entry.id}`}
                    type="url"
                    placeholder="https://alloy.yourdomain.com"
                  />
                </Field>
                <Link
                  to="/admin/deployment/DEP-001?step=operate"
                  className={cn(
                    buttonVariants({ variant: "secondary" }),
                    "w-full"
                  )}
                >
                  Select Deployment
                </Link>
              </div>
            );
          }
          return (
            <EntryCard
              key={entry.id}
              entry={entry}
              selected={selectedId === entry.id}
              onSelect={() => setSelectedId(entry.id)}
              actions={actions}
            />
          );
        })}
      </div>

      <TableWrapper>
        <Table>
          <thead>
            <tr className="border-b border-line bg-bg">
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">ID</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Customer</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">State</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Last Updated</th>
              <th className="px-4 py-[9px]" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {DEP_TABLE.map((d) => (
              <tr
                key={d.id}
                className="cursor-pointer border-t border-line transition-colors hover:bg-bg"
                onClick={() => {
                  navigate(
                    d.state === "Operating"
                      ? `/admin/deployment/${d.id}?step=operate`
                      : "/deployment"
                  );
                }}
              >
                <td className="px-4 py-3 text-[13px] font-semibold text-ink">{d.id}</td>
                <td className="px-4 py-3 text-[13px] font-semibold text-ink">{d.customer}</td>
                <td className="px-4 py-3">
                  <Badge variant={(d.badgeVariant as "info" | "success") ?? "neutral"}>{d.state}</Badge>
                </td>
                <td className="px-4 py-3 font-mono text-[12px] text-text">{d.lastUpdated}</td>
                <td className="px-4 py-3 text-right text-[12px] font-semibold text-primary">
                  {d.state === "Operating" ? "Open →" : "Run preflight →"}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>
    </div>
  );
}